<template>
  <v-menu
    v-model="open"
    offset-y
    left
    nudge-bottom="8"
    :close-on-content-click="false"
    :min-width="menuWidth.min"
    :max-width="menuWidth.max"
    content-class="rr-menu"
  >
    <template #activator="{ on, attrs }">
      <v-btn
        icon
        v-bind="attrs"
        class="rr-trigger"
        :class="{ 'rr-trigger--ring': needsAttention }"
        :aria-label="triggerLabel"
        :title="triggerLabel"
        v-on="on"
      >
        <v-badge
          :value="count > 0"
          :content="badgeText"
          color="#25D366"
          overlap
          offset-x="12"
          offset-y="12"
          class="rr-badge"
        >
          <i class="ph ph-alarm rr-trigger-icon"></i>
        </v-badge>
      </v-btn>
    </template>

    <div class="rr-panel">
      <header class="rr-head">
        <div>
          <p class="rr-head-title">Renewal reminders</p>
          <p class="rr-head-meta">
            <template v-if="loading">Checking your policies…</template>
            <template v-else>{{ checkedText }}</template>
          </p>
        </div>
        <v-btn icon small :loading="loading" title="Check again" @click="refresh(true)">
          <v-icon size="18" color="#64748B">mdi-refresh</v-icon>
        </v-btn>
      </header>

      <div v-if="!loaded && loading" class="rr-loading">
        <v-progress-circular indeterminate size="28" color="#00AEEF" />
        <span>Checking which policies are due for renewal…</span>
      </div>

      <template v-else>
        <div class="rr-callout" :class="`rr-callout--${calloutTone}`">
          <i class="ph rr-callout-icon" :class="calloutIcon"></i>
          <div>
            <p class="rr-callout-title">{{ headline.title }}</p>
            <p class="rr-callout-body">{{ headline.body }}</p>
          </div>
        </div>

        <div v-if="count" class="rr-scroll">
          <section v-if="groups.ended.length" class="rr-month">
            <p class="rr-month-title rr-month-title--ended">
              Cover has ended
              <span class="rr-month-count">{{ groups.ended.length }}</span>
            </p>
            <reminder-row
              v-for="r in groups.ended"
              :key="r.key"
              :reminder="r"
              @open="openReminder"
            />
          </section>

          <section v-for="month in groups.months" :key="month.key" class="rr-month">
            <p class="rr-month-title">
              {{ month.isCurrent ? `${month.title} · this month` : month.title }}
              <span class="rr-month-count">{{ month.count }}</span>
            </p>
            <div v-for="week in month.weeks" :key="week.key" class="rr-week">
              <p class="rr-week-title">
                {{ week.title }}
                <span class="rr-week-range">{{ week.range }}</span>
              </p>
              <reminder-row
                v-for="r in week.items"
                :key="r.key"
                :reminder="r"
                @open="openReminder"
              />
            </div>
          </section>

          <section v-if="groups.undated.length" class="rr-month">
            <p class="rr-month-title">Date to be confirmed</p>
            <reminder-row
              v-for="r in groups.undated"
              :key="r.key"
              :reminder="r"
              @open="openReminder"
            />
          </section>
        </div>

        <footer v-if="count" class="rr-foot">
          <span v-if="count > panelLimit" class="rr-foot-note">
            Showing the {{ panelLimit }} soonest of {{ count }}
          </span>
          <v-btn text small color="#184679" class="rr-foot-link" @click="viewAll">
            See all renewals
            <v-icon right size="16">mdi-arrow-right</v-icon>
          </v-btn>
        </footer>
      </template>
    </div>
  </v-menu>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { REMINDER_REFRESH_MS } from '@/store/modules/renewalReminders';
import {
  formatBadgeCount,
  groupReminders,
  reminderHeadline,
} from '@/utils/renewalReminders';
import { customerRenewalLocation, policyToSlug, saveRenewalToSession } from '@/utils/agentRenewal';
import { trackAgentInteraction } from '@/utils/agentAnalytics';
import ReminderRow from '@/components/renewals/RenewalReminderRow';

export default {
  name: 'RenewalReminderBell',
  components: { ReminderRow },
  props: {
    audience: {
      type: String,
      default: 'customer',
      validator: (v) => ['customer', 'agent'].includes(v),
    },
  },
  data: () => ({
    open: false,
    now: Date.now(),
    timer: null,
    clock: null,
    panelLimit: 25,
  }),
  computed: {
    ...mapState('renewalReminders', ['loading', 'loaded', 'fetchedAt']),
    ...mapGetters('renewalReminders', { reminders: 'actionable', summary: 'summary', count: 'count' }),
    groups() {
      return groupReminders(this.reminders.slice(0, this.panelLimit));
    },
    menuWidth() {
      const breakpoint = this.$vuetify && this.$vuetify.breakpoint;
      if (breakpoint && breakpoint.xsOnly) {
        const width = 'calc(100vw - 16px)';
        return { min: width, max: width };
      }
      return { min: 360, max: 400 };
    },
    headline() {
      return reminderHeadline(this.summary);
    },
    needsAttention() {
      return this.summary.ended + this.summary.urgent > 0;
    },
    calloutTone() {
      if (!this.count) return 'calm';
      if (this.needsAttention) return 'alert';
      return this.summary.soon ? 'warm' : 'calm';
    },
    calloutIcon() {
      if (!this.count) return 'ph-check-circle';
      return this.needsAttention ? 'ph-warning-circle' : 'ph-calendar-check';
    },
    badgeText() {
      return formatBadgeCount(this.count);
    },
    triggerLabel() {
      if (!this.count) return 'Renewal reminders';
      return `Renewal reminders: ${this.count} ${this.count === 1 ? 'policy' : 'policies'} up for renewal`;
    },
    checkedText() {
      if (!this.fetchedAt) return 'Not checked yet';
      const minutes = Math.floor((this.now - this.fetchedAt) / 60000);
      if (minutes < 1) return 'Checked just now';
      return `Checked ${minutes} min ago`;
    },
  },
  watch: {
    open(isOpen) {
      if (!isOpen) return;
      this.now = Date.now();
      this.refresh(false);
      if (this.audience === 'agent') {
        trackAgentInteraction(this, 'renewal_reminders_open', { count: this.count });
      }
    },
  },
  mounted() {
    this.refresh(false);
    this.timer = setInterval(() => this.refresh(false), REMINDER_REFRESH_MS);
    this.clock = setInterval(() => { this.now = Date.now(); }, 60000);
    document.addEventListener('visibilitychange', this.onVisibility);
  },
  beforeDestroy() {
    clearInterval(this.timer);
    clearInterval(this.clock);
    document.removeEventListener('visibilitychange', this.onVisibility);
  },
  methods: {
    refresh(force) {
      return this.$store.dispatch('renewalReminders/refresh', { force });
    },
    onVisibility() {
      if (document.visibilityState === 'visible') this.refresh(false);
    },
    openReminder(reminder) {
      this.open = false;
      const r = reminder.raw || {};
      if (this.audience === 'agent') {
        trackAgentInteraction(this, 'renewal_reminder_click', {
          policy_number: r.policyNo || 'unknown',
          days_to_renewal: reminder.days,
        });
        if (r.policyNo) {
          saveRenewalToSession(r, reminder.days != null && reminder.days < 0 ? 'lapsed' : 'pending');
          this.go({ name: 'AgentRenewPolicy', params: { policyNo: policyToSlug(r.policyNo) } });
        } else {
          this.go({ name: 'AgentRenewals' });
        }
        return;
      }
      if ((r.effectiveBatchNo || r.batchNo) && r.policyNo) {
        this.go(customerRenewalLocation(r, reminder.days));
      } else {
        this.go({ name: 'CustomerRenewals' });
      }
    },
    viewAll() {
      this.open = false;
      this.go({ name: this.audience === 'agent' ? 'AgentRenewals' : 'CustomerRenewals' });
    },
    go(location) {
      if (this.$route.name === location.name && !location.params) return;
      this.$router.push(location).catch(() => {});
    },
  },
};
</script>

<style scoped>
.rr-trigger-icon {
  font-size: 22px;
  color: #184679;
}

.rr-trigger--ring .rr-trigger-icon {
  animation: rr-ring 2.4s ease-in-out 1s 3;
  transform-origin: 50% 10%;
}

@keyframes rr-ring {
  0%, 60%, 100% { transform: rotate(0); }
  10%, 30%, 50% { transform: rotate(-12deg); }
  20%, 40% { transform: rotate(12deg); }
}

.rr-badge >>> .v-badge__badge {
  font-weight: 800;
  font-size: 10px;
  min-width: 18px;
  height: 18px;
  padding: 3px 5px;
  border: 2px solid #fff;
  box-sizing: content-box;
}

.rr-panel {
  background: #fff;
  border-radius: 14px;
  overflow: hidden;
}

.rr-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 14px 10px 18px;
  border-bottom: 1px solid #EEF2F6;
}

.rr-head-title {
  margin: 0;
  font-size: 0.98rem;
  font-weight: 800;
  color: #1E4276;
}

.rr-head-meta {
  margin: 2px 0 0;
  font-size: 0.72rem;
  color: #94A3B8;
}

.rr-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 36px 20px;
  color: #64748B;
  font-size: 0.82rem;
}

.rr-callout {
  display: flex;
  gap: 10px;
  margin: 12px 14px 4px;
  padding: 10px 12px;
  border-radius: 10px;
}

.rr-callout--alert { background: #FFF5F5; color: #9B2C2C; }
.rr-callout--warm { background: #FFFAF0; color: #9C4221; }
.rr-callout--calm { background: #F0FFF4; color: #276749; }

.rr-callout-icon {
  font-size: 22px;
  flex-shrink: 0;
  margin-top: 1px;
}

.rr-callout-title {
  margin: 0;
  font-size: 0.82rem;
  font-weight: 800;
}

.rr-callout-body {
  margin: 2px 0 0;
  font-size: 0.78rem;
  line-height: 1.4;
}

.rr-scroll {
  max-height: min(420px, calc(100vh - 260px));
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: 4px 0 8px;
}

.rr-month {
  padding: 6px 0 0;
}

.rr-month-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 18px 2px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #1E4276;
}

.rr-month-title--ended { color: #C53030; }

.rr-month-count {
  padding: 1px 7px;
  border-radius: 999px;
  background: #EEF2F6;
  color: #475569;
  font-size: 0.68rem;
  letter-spacing: 0;
}

.rr-week-title {
  margin: 8px 18px 2px;
  font-size: 0.74rem;
  font-weight: 700;
  color: #475569;
}

.rr-week-range {
  margin-left: 6px;
  font-weight: 500;
  color: #94A3B8;
}

.rr-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 8px 10px 8px 18px;
  border-top: 1px solid #EEF2F6;
}

.rr-foot-note {
  font-size: 0.72rem;
  color: #94A3B8;
}

.rr-foot-link {
  margin-left: auto;
  font-weight: 700;
  text-transform: none;
  letter-spacing: 0;
}
</style>

<style>
.rr-menu {
  border-radius: 14px !important;
  box-shadow: 0 18px 48px rgba(15, 39, 71, 0.18) !important;
}
</style>
